import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { concat, from, of, Observable } from 'rxjs';
import { switchMap, concatMap, toArray, map } from 'rxjs/operators';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import {
  ChecklistTemplateService, ChecklistTemplate, ChecklistItem,
  CreateChecklistTemplateDto, UpdateChecklistTemplateDto
} from '../../services/checklist-template.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AuthService } from '../../services/auth.service';

interface FormItem {
  id?: string;
  question: string;
  displayOrder: number;
  isActive: boolean;
  originalQuestion?: string;
  originalIsActive?: boolean;
}

@Component({
  selector: 'app-checklist-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    ConfirmDialog,
    Select,
    Tooltip
  ],
  providers: [ConfirmationService],
  templateUrl: './checklist-management.component.html',
  styleUrl: './checklist-management.component.scss'
})
export class ChecklistManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  templates: ChecklistTemplate[] = [];
  filteredTemplates: ChecklistTemplate[] = [];
  expandedRows: Record<string, boolean> = {};
  loading = false;
  searchText = '';
  selectedStatusFilter: string | null = 'active';

  rows = 50;
  first = 0;
  totalRecords = 0;

  statusFilterOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  // ── Create/Edit template dialog ──────────────────────────────────────────────
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  saving = false;
  templateId = '';
  templateName = '';
  templateIsActive = true;
  nameTouched = false;

  // Items inside the create dialog
  formItems: FormItem[] = [];
  newItemQuestion = '';
  deletedItemIds: string[] = [];

  // ── Inline item editing (in expanded row) ────────────────────────────────────
  editingItemId: string | null = null;
  editingQuestion = '';
  savingItemId: string | null = null;
  newInlineInputs: Record<string, string> = {};   // keyed by templateId
  addingItemForTemplate: string | null = null;    // templateId currently being added to

  get totalCount() { return this.templates.length; }
  get activeCount() { return this.templates.filter(t => t.isActive).length; }
  get inactiveCount() { return this.templates.filter(t => !t.isActive).length; }
  get totalItemCount() { return this.templates.reduce((sum, t) => sum + (t.itemCount ?? 0), 0); }

  constructor(
    private service: ChecklistTemplateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Checklist');
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadData();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applyFilters();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.openAddDialog());
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!user?.isAdmin);
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData(): void {
    this.loading = true;
    this.service.getAll().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: data => {
        this.templates = data.map(t => ({ ...t, items: t.items ?? [] }));
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load checklists.' });
        this.loading = false;
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.templates];
    if (this.searchText?.trim()) {
      const q = this.searchText.trim().toLowerCase();
      filtered = filtered.filter(t => t.name.toLowerCase().includes(q));
    }
    if (this.selectedStatusFilter === 'active') {
      filtered = filtered.filter(t => t.isActive);
    } else if (this.selectedStatusFilter === 'inactive') {
      filtered = filtered.filter(t => !t.isActive);
    }
    this.filteredTemplates = filtered;
    this.totalRecords = filtered.length;
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText?.trim() || (this.selectedStatusFilter !== null && this.selectedStatusFilter !== 'active'));
  }

  clearFilters(): void {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.selectedStatusFilter = 'active';
    this.applyFilters();
  }

  // ─── Row expansion ────────────────────────────────────────────────────────────

  toggleRow(template: ChecklistTemplate): void {
    if (this.expandedRows[template.id]) {
      delete this.expandedRows[template.id];
      // cancel any in-progress inline edit for this template
      if (this.editingItemId) this.cancelEditItem();
    } else {
      this.expandedRows = { ...this.expandedRows, [template.id]: true };
    }
  }

  isExpanded(template: ChecklistTemplate): boolean {
    return !!this.expandedRows[template.id];
  }

  // ─── Inline item editing ──────────────────────────────────────────────────────

  startEditItem(item: ChecklistItem): void {
    this.editingItemId = item.id;
    this.editingQuestion = item.question;
  }

  cancelEditItem(): void {
    this.editingItemId = null;
    this.editingQuestion = '';
  }

  saveEditItem(template: ChecklistTemplate, item: ChecklistItem): void {
    const q = this.editingQuestion.trim();
    if (!q) return;
    this.savingItemId = item.id;
    this.service.updateItem(template.id, item.id, {
      question: q,
      displayOrder: item.displayOrder,
      isActive: item.isActive
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.templates = this.templates.map(t => {
          if (t.id !== template.id) return t;
          return { ...t, items: t.items.map(i => i.id === updated.id ? updated : i) };
        });
        this.applyFilters();
        this.editingItemId = null;
        this.savingItemId = null;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update item.' });
        this.savingItemId = null;
      }
    });
  }

  onEditItemKeydown(event: KeyboardEvent, template: ChecklistTemplate, item: ChecklistItem): void {
    if (event.key === 'Enter') { event.preventDefault(); this.saveEditItem(template, item); }
    if (event.key === 'Escape') this.cancelEditItem();
  }

  toggleItemActive(template: ChecklistTemplate, item: ChecklistItem): void {
    this.service.updateItem(template.id, item.id, {
      question: item.question,
      displayOrder: item.displayOrder,
      isActive: !item.isActive
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.templates = this.templates.map(t => {
          if (t.id !== template.id) return t;
          return { ...t, items: t.items.map(i => i.id === updated.id ? updated : i) };
        });
        this.applyFilters();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update item.' })
    });
  }

  deleteItemInline(template: ChecklistTemplate, item: ChecklistItem): void {
    this.confirmationService.confirm({
      message: `Delete item "${item.question}"?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.service.deleteItem(template.id, item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.templates = this.templates.map(t => {
              if (t.id !== template.id) return t;
              const items = t.items.filter(i => i.id !== item.id);
              return { ...t, items, itemCount: items.length };
            });
            this.applyFilters();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete item.' })
        });
      }
    });
  }

  // ─── Inline add item ──────────────────────────────────────────────────────────

  getNewInlineInput(templateId: string): string {
    return this.newInlineInputs[templateId] ?? '';
  }

  setNewInlineInput(templateId: string, value: string): void {
    this.newInlineInputs = { ...this.newInlineInputs, [templateId]: value };
  }

  addItemInline(template: ChecklistTemplate): void {
    const q = (this.newInlineInputs[template.id] ?? '').trim();
    if (!q) return;
    this.addingItemForTemplate = template.id;
    this.service.addItem(template.id, {
      question: q,
      displayOrder: (template.items?.length ?? 0) + 1
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: created => {
        this.templates = this.templates.map(t => {
          if (t.id !== template.id) return t;
          const items = [...t.items, created];
          return { ...t, items, itemCount: items.length };
        });
        this.applyFilters();
        this.newInlineInputs = { ...this.newInlineInputs, [template.id]: '' };
        this.addingItemForTemplate = null;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add item.' });
        this.addingItemForTemplate = null;
      }
    });
  }

  onInlineAddKeydown(event: KeyboardEvent, template: ChecklistTemplate): void {
    if (event.key === 'Enter') { event.preventDefault(); this.addItemInline(template); }
  }

  // ─── Create dialog ────────────────────────────────────────────────────────────

  openAddDialog(): void {
    this.dialogMode = 'add';
    this.templateId = '';
    this.templateName = '';
    this.templateIsActive = true;
    this.nameTouched = false;
    this.formItems = [];
    this.newItemQuestion = '';
    this.deletedItemIds = [];
    this.displayDialog = true;
  }

  openEditDialog(template: ChecklistTemplate): void {
    this.dialogMode = 'edit';
    this.templateId = template.id;
    this.templateName = template.name;
    this.templateIsActive = template.isActive;
    this.nameTouched = false;
    this.formItems = (template.items ?? []).map(i => ({
      id: i.id,
      question: i.question,
      displayOrder: i.displayOrder,
      isActive: i.isActive,
      originalQuestion: i.question,
      originalIsActive: i.isActive
    }));
    this.newItemQuestion = '';
    this.deletedItemIds = [];
    this.displayDialog = true;
  }

  hideDialog(): void {
    this.displayDialog = false;
  }

  // ─── Form items (create dialog) ───────────────────────────────────────────────

  addItemToForm(): void {
    const q = this.newItemQuestion.trim();
    if (!q) return;
    this.formItems = [...this.formItems, {
      question: q,
      displayOrder: this.formItems.length + 1,
      isActive: true
    }];
    this.newItemQuestion = '';
  }

  onNewItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') { event.preventDefault(); this.addItemToForm(); }
  }

  removeItemFromForm(index: number): void {
    const item = this.formItems[index];
    if (item.id) {
      this.deletedItemIds = [...this.deletedItemIds, item.id];
    }
    this.formItems = this.formItems
      .filter((_, i) => i !== index)
      .map((fi, i) => ({ ...fi, displayOrder: i + 1 }));
  }

  // ─── Save ─────────────────────────────────────────────────────────────────────

  save(): void {
    this.nameTouched = true;
    if (!this.templateName.trim()) return;
    if (this.newItemQuestion.trim()) {
      this.addItemToForm();
    }
    this.saving = true;
    this.dialogMode === 'add' ? this.saveCreate() : this.saveEdit();
  }

  private saveCreate(): void {
    const dto: CreateChecklistTemplateDto = {
      name: this.templateName.trim()
    };
    this.service.create(dto).pipe(
      switchMap(created => {
        if (!this.formItems.length) return of({ created, items: [] as ChecklistItem[] });
        return from(this.formItems).pipe(
          concatMap(item => this.service.addItem(created.id, {
            question: item.question.trim(),
            displayOrder: item.displayOrder
          })),
          toArray(),
          map(items => ({ created, items }))
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ created, items }) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Checklist created.' });
        this.templates = [...this.templates, { ...created, items, itemCount: items.length }];
        this.applyFilters();
        this.displayDialog = false;
        this.saving = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save checklist.' });
        this.saving = false;
      }
    });
  }

  private saveEdit(): void {
    const allOps: Observable<unknown>[] = [
      this.service.update(this.templateId, { name: this.templateName.trim(), isActive: this.templateIsActive }),
      ...this.deletedItemIds.map(id => this.service.deleteItem(this.templateId, id)),
      ...this.formItems
        .filter(i => i.id && (
          i.question.trim() !== (i.originalQuestion ?? '') ||
          i.isActive !== i.originalIsActive
        ))
        .map(i => this.service.updateItem(this.templateId, i.id!, {
          question: i.question.trim(),
          displayOrder: i.displayOrder,
          isActive: i.isActive
        })),
      ...this.formItems
        .filter(i => !i.id)
        .map(i => this.service.addItem(this.templateId, {
          question: i.question.trim(),
          displayOrder: i.displayOrder
        }))
    ];

    concat(...allOps).pipe(
      toArray(),
      switchMap(() => this.service.getById(this.templateId)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: refreshed => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Checklist updated.' });
        this.templates = this.templates.map(t =>
          t.id === refreshed.id ? { ...refreshed, items: refreshed.items ?? [] } : t
        );
        this.applyFilters();
        this.displayDialog = false;
        this.saving = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update checklist.' });
        this.saving = false;
      }
    });
  }

  // ─── Toggle / Delete template ──────────────────────────────────────────────────

  toggleStatus(template: ChecklistTemplate): void {
    const dto: UpdateChecklistTemplateDto = {
      name: template.name,
      isActive: !template.isActive
    };
    this.service.update(template.id, dto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: `Checklist ${updated.isActive ? 'activated' : 'deactivated'}.` });
        this.templates = this.templates.map(t => t.id === updated.id ? { ...updated, items: t.items } : t);
        this.applyFilters();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status.' })
    });
  }

  deleteTemplate(template: ChecklistTemplate): void {
    this.confirmationService.confirm({
      message: `Delete "${template.name}" and all its ${template.itemCount} item(s)?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.service.delete(template.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Checklist deleted.' });
            this.templates = this.templates.filter(t => t.id !== template.id);
            this.applyFilters();
          },
          error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete checklist.' })
        });
      }
    });
  }
}
